import { useEffect, useId, useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  ArrowRight,
  Megaphone,
  Handshake,
  Radio,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { motionReduced } from '@/a11y/preferences'
import { A11Y_CHANGE_EVENT } from '@/a11y/useA11yPreferences'
import { lifespans } from '@/config/lifespans'
import { LifespanVenn } from '@/components/ui/LifespanVenn'

interface Pillar {
  name: string
  lead: string
  body: string
  icon: LucideIcon
}

// Advocate / Activate / Amplify — copy per Website Review. Moved below the
// LifeSpan framework in V2.
const pillars: Pillar[] = [
  {
    name: 'Advocate',
    lead: 'Reframe aging as the economic and social opportunity of our time, covering a lifespan',
    body: 'Changing the narrative and eliminating ageism.',
    icon: Megaphone,
  },
  {
    name: 'Activate',
    lead: 'Move from conversation to commitment',
    body: 'Brokering the public-private partnerships that turn longevity vision into real-world strategy.',
    icon: Handshake,
  },
  {
    name: 'Amplify',
    lead: 'Scale what works',
    body: 'Distributing proven innovations and thought leadership that transforms how society approaches aging.',
    icon: Radio,
  },
]

/**
 * Our Work — one combined section (Website Review V2). The LifeSpan framework
 * leads, shown as an interactive Venn diagram to convey how the five spans
 * overlap and work together, followed by Advocate / Activate / Amplify.
 */
const AUTOPLAY_MS = 3000

export function OurWork() {
  const headingId = useId()
  const [activeId, setActiveId] = useState(lifespans[0].id)
  const [paused, setPaused] = useState(false)
  const active = lifespans.find((l) => l.id === activeId) ?? lifespans[0]

  // Auto-advance through the spans every few seconds. Re-keying on activeId
  // gives a fresh interval after each change (auto or manual), and it pauses
  // while the visitor hovers/focuses the diagram. Respects reduced-motion.
  // No auto-advance when motion should be reduced: OS setting or the
  // visitor's choice in the Accessibility menu (re-checked when it changes).
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    const sync = () => setReducedMotion(motionReduced())
    sync()
    window.addEventListener(A11Y_CHANGE_EVENT, sync)
    return () => window.removeEventListener(A11Y_CHANGE_EVENT, sync)
  }, [])

  useEffect(() => {
    if (paused || reducedMotion) return
    const timer = setTimeout(() => {
      const i = lifespans.findIndex((l) => l.id === activeId)
      setActiveId(lifespans[(i + 1) % lifespans.length].id)
    }, AUTOPLAY_MS)
    return () => clearTimeout(timer)
  }, [activeId, paused, reducedMotion])

  return (
    <section
      id="work"
      aria-labelledby={headingId}
      className="bg-surface py-section-y"
    >
      <div className="mx-auto max-w-[88rem] px-6 sm:px-10">
        <header className="mx-auto max-w-3xl text-center">
          <h2
            id={headingId}
            className="font-display text-display-2 font-extrabold text-ink-deep"
          >
            Our Work.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink-slate sm:text-lg">
            Aging is not a destination; it is a lifelong journey that begins at
            birth. The decisions made today about health, work, wealth, and
            care will determine whether longer lives become a societal
            challenge or a great opportunity for people to thrive. As longevity
            transforms markets, talent, and consumer needs, GIA Longevity is
            convening leaders across generations to co-design a future where
            living longer also means living better.
          </p>
        </header>

        {/* LifeSpan framework — centered Venn diagram with the detail below.
            Auto-advances; pauses while the visitor interacts. */}
        <div
          className="mt-14"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <LifespanVenn activeId={activeId} onSelect={setActiveId} />

          {/* All five panels share one grid cell, so the block keeps the
              height of the tallest and the page below never jumps while the
              diagram auto-advances. Inactive panels are hidden from AT/tab. */}
          <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 text-center">
            {lifespans.map((span) => {
              const isActive = span.id === active.id
              return (
                <article
                  key={span.id}
                  aria-hidden={!isActive}
                  className={cn(
                    'transition-opacity duration-300 ease-out-quart [grid-area:1/1]',
                    isActive ? 'opacity-100' : 'invisible opacity-0',
                  )}
                >
                  <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                    <span.icon
                      className="h-8 w-8"
                      strokeWidth={1.5}
                      style={{ color: span.ink }}
                      aria-hidden
                    />
                    {/* Span name — larger than the descriptor below (Website Review V3) */}
                    <span
                      className="min-w-0 break-words font-display text-3xl font-extrabold sm:text-4xl"
                      style={{ color: span.ink }}
                    >
                      {span.name}
                      <sup className="ml-0.5 align-super text-sm" aria-label="trademark">™</sup>
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold leading-snug text-ink-deep sm:text-2xl">
                    {span.tagline}
                  </h3>
                  <p className="mx-auto mt-3 max-w-prose text-base leading-relaxed text-ink-slate">
                    {span.description}
                  </p>
                  <a
                    href="#contact"
                    className="mt-4 inline-flex min-h-11 flex-wrap items-center justify-center gap-2 text-sm font-bold uppercase tracking-[0.08em] text-ink-deep hover:text-brand-strong"
                  >
                    Talk to us about {span.name}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </a>
                </article>
              )
            })}
          </div>
        </div>

        {/* Advocate / Activate / Amplify — moved below the framework (V2) */}
        <div className="mt-20 border-t border-hairline pt-14">
          <p className="max-w-3xl font-display text-2xl font-bold leading-snug text-ink-deep sm:text-3xl">
            GIA Longevity is bringing research and ideas to reality while
            changing the narrative about aging.
          </p>

          <ul
            role="list"
            className="mt-12 grid grid-cols-1 gap-y-12 md:grid-cols-3 md:gap-x-12"
          >
            {pillars.map((pillar) => {
              const Icon = pillar.icon
              return (
                <li key={pillar.name} className="border-t-2 border-ink-deep pt-6">
                  <Icon className="h-7 w-7 text-brand-strong" strokeWidth={1.5} aria-hidden />
                  <h3 className="mt-5 font-display text-2xl font-extrabold text-ink-deep">
                    {pillar.name}
                  </h3>
                  <p className="mt-3 font-display text-base font-bold leading-snug text-ink-deep sm:text-lg">
                    {pillar.lead}.
                  </p>
                  <p className="mt-3 max-w-xs text-base leading-relaxed text-ink-slate">
                    {pillar.body}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
    </section>
  )
}
